/**
 * Disputes — chargebacks on Titan Treasure from the newest Finix export:
 * what needs a response and by when, what was won and lost and why, and which
 * Idenza accounts the cardholders tie to. All time; the export is cumulative.
 * Laid out like the Titan Treasure fraud review pages (see the review-page
 * blocks in ttKit). Data: GET /analytics/tt/disputes, /disputes/names,
 * /disputes/list.
 */
import React, { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  CalendarDays,
  CircleAlert,
  CircleCheck,
  CircleX,
  Clock,
  Hourglass,
  Link2,
  Percent,
  RotateCcw,
  TriangleAlert,
} from "lucide-react";
import { Alert, Segmented } from "../../../design/primitives";
import { useTTQuery } from "../ttApi";
import { count, dateTime, humanize, money, moneyExact, monthLabel, pct, utcDate } from "../ttFormat";
import {
  B,
  ChartBox,
  Finding,
  Foot,
  INK,
  LoadState,
  Lede,
  Pager,
  Pill,
  ReviewCard,
  ReviewGrid,
  SearchBox,
  Select,
  TONE,
  Table,
  Tile,
  Tiles,
  Tools,
  VerdictPill,
  Wrap,
  axisProps,
  linkButton,
} from "../ttKit";

const PAGE_SIZE = 20;

const STAGE_LABELS = {
  CHARGEBACK: "First chargeback",
  PRE_ARBITRATION: "Pre-arbitration",
  ARBITRATION: "Arbitration",
  RDR: "Refunded by Visa RDR",
};
const stageLabel = (s) => STAGE_LABELS[s] || humanize(s);
const STATE_TONES = { PENDING: "amber", WON: "green", LOST: "red" };

const NAME_SORTS = [
  { value: "usd", label: "Value" },
  { value: "disputes", label: "Disputes" },
  { value: "last_txn", label: "Latest" },
];

const ALL = { value: "", label: "All" };
const STATE_OPTIONS = [ALL, ...["PENDING", "WON", "LOST"].map((v) => ({ value: v, label: humanize(v) }))];
const RESPONSE_OPTIONS = [
  ALL,
  ...["NEEDS_RESPONSE", "RESPONDED", "NO_RESPONSE_ALLOWED", "ACCEPTED"].map((v) => ({ value: v, label: humanize(v) })),
];
const STAGE_OPTIONS = [ALL, ...Object.entries(STAGE_LABELS).map(([value, label]) => ({ value, label }))];
const LINK_OPTIONS = [ALL, { value: "1", label: "Tied to an account" }];

const shownOf = (shown, all, noun) =>
  shown === all ? `${count(all)} ${noun}` : `${count(shown)} of ${count(all)} ${noun}`;

export default function DisputesPanel({ fresh, openAccount }) {
  const overview = useTTQuery("disputes", { fresh });
  return (
    <LoadState state={overview} height={360}>
      {(d) =>
        d.loaded ? (
          <>
            <Overview d={d} openAccount={openAccount} />
            <NamesSection d={d} fresh={fresh} openAccount={openAccount} />
            <DisputeList d={d} fresh={fresh} openAccount={openAccount} />
            <Foot style={{ marginTop: 0 }}>
              Disputes are tied to accounts two ways. The Finix transfer ID is exact, but exists only for deposits
              Idenza scored, from 15 Sep 2026. The cardholder name is typed by the player at deposit and checked by
              nobody, so a name match is strong evidence, not a confirmed identity. Disputes are only as current as the
              last export copied in.
            </Foot>
          </>
        ) : (
          <Alert tone="info" title="No dispute export loaded" description={d.message} />
        )
      }
    </LoadState>
  );
}

function Overview({ d, openAccount }) {
  const k = d.kpis;
  const s = d.source;
  const owed = k.needs_response;
  return (
    <>
      <Foot style={{ margin: "0 0 10px" }}>
        Finix export {s.file}, read {dateTime(s.loaded_at)}. Disputes filed {utcDate(s.filed_from)} to{" "}
        {utcDate(s.filed_to)}; accounts as of {dateTime(s.accounts_as_of)}.
      </Foot>
      {s.link_error && (
        <Alert
          tone="warning"
          title="Disputes are shown without account links"
          description={s.link_error}
          style={{ marginBottom: 14 }}
        />
      )}

      <Lede>
        <>
          <B>
            {count(k.n)} disputes are on file to {utcDate(s.filed_to)}, worth {money(k.usd)}.
          </B>{" "}
          {count(k.pending)} are still pending, {count(k.lost)} were lost and {count(k.won)} won.{" "}
          {count(k.fraud.n)} of them carry a fraud reason code.
        </>
        <>
          They came from <B>{count(k.names)}</B> cardholder names, and the top five hold{" "}
          <B>{pct(k.top5_names_share)}</B> of the disputed value.
          {k.returned.names > 0 && (
            <>
              {" "}
              <B>{count(k.returned.names)}</B> of those names were still active on an Idenza account after their
              disputed deposits.
            </>
          )}
        </>
      </Lede>

      <Tiles>
        <Tile tone="red" icon={CircleAlert} label="Disputes on file" value={money(k.usd)} sub={`${count(k.n)} disputes`} />
        <Tile
          tone={owed.n ? "red" : "green"}
          icon={Clock}
          label="Needs a response"
          value={count(owed.n)}
          sub={
            owed.n
              ? `${money(owed.usd)} · next due ${utcDate(owed.next_respond_by, false)}` +
                (owed.overdue ? ` · ${count(owed.overdue)} overdue` : "")
              : "Nothing owed"
          }
        />
        <Tile tone="amber" icon={Hourglass} label="Pending" value={money(k.pending_usd)} sub={`${count(k.pending)} awaiting the network`} />
        <Tile tone="red" icon={CircleX} label="Lost" value={money(k.lost_usd)} sub={`${count(k.lost)} disputes`} />
        <Tile tone="green" icon={CircleCheck} label="Won" value={money(k.won_usd)} sub={`${count(k.won)} disputes`} />
      </Tiles>
      <Tiles>
        <Tile tone="blue" icon={Percent} label="Win rate" value={pct(k.win_rate)} sub="By value, of decided disputes" />
        <Tile
          tone="red"
          icon={TriangleAlert}
          label="Raised as fraud"
          value={pct(k.n ? k.fraud.n / k.n : null)}
          sub={`${count(k.fraud.n)} disputes · ${money(k.fraud.usd)}`}
        />
        <Tile tone="amber" icon={CalendarDays} label="Filed in the last 7 days" value={count(k.filed_7d.n)} sub={money(k.filed_7d.usd)} />
        <Tile
          tone="blue"
          icon={Link2}
          label="Tied to Idenza accounts"
          value={count(k.on_accounts.n)}
          sub={`${count(k.linked.n)} by transfer, the rest by cardholder name`}
        />
        <Tile
          tone="red"
          icon={RotateCcw}
          label="Still active after a dispute"
          value={money(k.returned.usd)}
          sub={`${count(k.returned.n)} disputes under ${count(k.returned.names)} names`}
        />
      </Tiles>

      <ReviewCard
        eyebrow="Queue"
        title="Needs a response"
        desc="Soonest deadline first. A dispute with no response by its date is lost."
        tag={owed.n ? "Action required" : "Nothing owed"}
        tagTone={owed.n ? "red" : "green"}
      >
        <Table
          columns={QUEUE_COLUMNS(openAccount)}
          rows={d.queue.map((r) => ({ key: r.id, ...r }))}
          emptyState="Nothing needs a response."
        />
      </ReviewCard>

      <ReviewCard eyebrow="Findings" title="What the export shows" tag={k.vfmp.n ? "Monitoring programme" : null} tagTone="red">
        <Findings d={d} />
      </ReviewCard>

      {d.returned_names.length > 0 && (
        <ReviewCard
          eyebrow="The link"
          title="Disputed names still active on Idenza accounts"
          desc={
            "Each of these cardholder names has disputes, and an Idenza account tied to the name was used after " +
            "the last disputed deposit. Nothing in the dispute export alone connects them."
          }
          tag="Repeat"
          tagTone="red"
        >
          {d.returned_names.map((n, i) => (
            <Finding key={n.name} tone="red" title={n.name} last={i === d.returned_names.length - 1}>
              <B>{count(n.n)} disputes</B> worth <B>{money(n.usd)}</B> on deposits from {utcDate(n.first_txn, false)} to{" "}
              {utcDate(n.last_txn)}, {pct(k.usd ? n.usd / k.usd : null)} of all disputed value; {count(n.lost)} lost,{" "}
              {count(n.pending)} pending. Then active on{" "}
              {n.accounts.map((a, j) => (
                <React.Fragment key={a.email}>
                  {j > 0 && "; "}
                  <AccountButton email={a.email} onOpen={openAccount} /> <VerdictPill verdict={a.verdict} />{" "}
                  {a.first_seen ? `${utcDate(a.first_seen, false)} to ` : "until "}
                  {utcDate(a.last_seen)}
                </React.Fragment>
              ))}
              .
            </Finding>
          ))}
        </ReviewCard>
      )}

      <ReviewGrid>
        <ReviewCard
          eyebrow="Lag"
          title="Disputes by deposit month"
          desc={
            `Disputes arrive a median ${count(k.lag_days.median)} days after the deposit, and 90% within ` +
            `${count(k.lag_days.p90)} days, so recent months are still filling in.`
          }
        >
          <ChartBox height={240}>
            <ResponsiveContainer>
              <BarChart
                data={d.occurred_monthly.map((m) => ({ label: monthLabel(m.month), Disputes: m.n, usd: m.usd }))}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid vertical={false} stroke={INK.line} />
                <XAxis dataKey="label" {...axisProps} />
                <YAxis {...axisProps} allowDecimals={false} width={40} />
                <Tooltip formatter={(v, name, p) => [`${count(v)} · ${money(p.payload.usd)}`, name]} />
                <Bar dataKey="Disputes" fill={TONE.blue.fg} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartBox>
        </ReviewCard>
        <ReviewCard eyebrow="Breakdown" title="By reason" desc="Win rate is by value, of decided disputes.">
          <Table
            columns={BREAKDOWN_COLUMNS("reason", "Reason", humanize)}
            rows={d.by_reason.map((r) => ({ key: r.reason, ...r }))}
          />
        </ReviewCard>
      </ReviewGrid>

      <ReviewGrid>
        <ReviewCard
          eyebrow="Breakdown"
          title="By stage"
          desc="Refunded by Visa RDR means the money went back automatically; those disputes cannot be answered."
        >
          <Table
            columns={BREAKDOWN_COLUMNS("stage", "Stage", stageLabel)}
            rows={d.by_stage.map((r) => ({ key: r.stage, ...r }))}
          />
        </ReviewCard>
        <ReviewCard eyebrow="Breakdown" title="By card network">
          <Table
            columns={BREAKDOWN_COLUMNS("network", "Network", (v) => v || "Unknown")}
            rows={d.by_network.map((r) => ({ key: r.network || "unknown", ...r }))}
          />
        </ReviewCard>
      </ReviewGrid>

      <ReviewCard eyebrow="Card networks" title="What the networks said" desc="The most common dispute messages.">
        <Table
          columns={MESSAGE_COLUMNS}
          rows={d.by_message.map((m, i) => ({ key: `${m.reason}-${i}`, ...m }))}
        />
      </ReviewCard>
    </>
  );
}

/** The few facts that matter most in this export, as review-page findings. */
function Findings({ d }) {
  const k = d.kpis;
  const fraud = d.by_reason.find((r) => r.reason === "FRAUD");
  const quality = d.by_reason.find((r) => r.reason === "QUALITY");
  const busiest = d.filed_weekly.reduce((top, w) => (!top || w.n > top.n ? w : top), null);
  const items = [
    k.vfmp.n > 0 && {
      tone: "amber",
      title: `${pct(k.vfmp.n / k.n)} come from Visa's Fraud Monitoring Program`,
      body: (
        <>
          <B>{count(k.vfmp.n)}</B> disputes worth <B>{money(k.vfmp.usd)}</B>. While Titan Treasure is enrolled, Visa
          tells the card's bank to file a chargeback whether or not the cardholder asked for one.{" "}
          <B>{count(k.vfmp_rdr.n)}</B> of them ({money(k.vfmp_rdr.usd)}) were refunded automatically through Visa RDR,
          so they count as lost and cannot be answered.
        </>
      ),
    },
    fraud && {
      tone: "red",
      title: `Fraud disputes: ${pct(fraud.win_rate)} won by value`,
      body: (
        <>
          <B>{count(fraud.n)}</B> disputes worth <B>{money(fraud.usd)}</B>: {count(fraud.won)} won, {count(fraud.lost)}{" "}
          lost and {count(fraud.pending)} pending.
          {quality?.win_rate != null && ` Quality disputes are won ${pct(quality.win_rate)} of the time by value.`}
        </>
      ),
    },
    busiest && {
      tone: "amber",
      title: `Busiest week: ${count(busiest.n)} disputes filed`,
      body: (
        <>
          In the week of <B>{utcDate(busiest.week)}</B>, worth <B>{money(busiest.usd)}</B>, {count(busiest.fraud)} of
          them fraud. Disputes arrive a median <B>{count(k.lag_days.median)} days</B> after the deposit, so deposits
          from the weeks before are still reporting.
        </>
      ),
    },
    {
      tone: "blue",
      title: "On deposits Idenza scored",
      body: (
        <>
          <B>{count(k.since_scoring.n)}</B> disputes ({money(k.since_scoring.usd)}) are on deposits made since scoring
          began on 15 Sep. <B>{count(k.linked.n)}</B> are tied to the exact deposit by Finix transfer ID.
        </>
      ),
    },
  ].filter(Boolean);
  return items.map((f, i) => (
    <Finding key={f.title} tone={f.tone} title={f.title} last={i === items.length - 1}>
      {f.body}
    </Finding>
  ));
}

function NamesSection({ d, fresh, openAccount }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("usd");
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [q, sort]);
  const names = useTTQuery("disputes/names", { q, sort, page, limit: PAGE_SIZE, fresh });
  const k = d.kpis;
  return (
    <ReviewCard
      eyebrow="Disputes"
      title="Every cardholder name with a dispute"
      desc={
        `Grouped by the name on the disputed card. The top 5 of ${count(k.names)} names hold ` +
        `${pct(k.top5_names_share)} of the value. Returned marks a name whose Idenza account was used after its ` +
        "last disputed deposit."
      }
      tag={k.returned.names ? `${count(k.returned.names)} returned` : null}
      tagTone="red"
    >
      <Tools count={names.data && shownOf(names.data.total, k.names, "names")}>
        <SearchBox value={q} onChange={setQ} placeholder="Search names or accounts" width={260} />
        <Segmented size="sm" options={NAME_SORTS} value={sort} onChange={setSort} />
      </Tools>
      <LoadState state={names}>
        {(n) => (
          <>
            <Table
              columns={NAME_COLUMNS(openAccount)}
              rows={n.rows.map((r) => ({ key: r.name, ...r }))}
              emptyState={q ? "No name matches." : "No disputes."}
            />
            <Pager page={page} limit={PAGE_SIZE} total={n.total} onPage={setPage} />
          </>
        )}
      </LoadState>
    </ReviewCard>
  );
}

function DisputeList({ d, fresh, openAccount }) {
  const [filters, setFilters] = useState({ state: "", reason: "", response: "", stage: "", linked: "" });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [filters, q]);
  const list = useTTQuery("disputes/list", { ...filters, q, page, limit: PAGE_SIZE, fresh });
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const reasonOptions = [ALL, ...d.by_reason.map((r) => ({ value: r.reason, label: humanize(r.reason) }))];

  return (
    <ReviewCard eyebrow="Disputes" title="All disputes" desc="Newest filed first.">
      <Tools count={list.data && shownOf(list.data.total, d.kpis.n, "disputes")}>
        <SearchBox value={q} onChange={setQ} placeholder="Name, case, transfer or account" width={260} />
        <Select label="State" value={filters.state} onChange={set("state")} options={STATE_OPTIONS} />
        <Select label="Reason" value={filters.reason} onChange={set("reason")} options={reasonOptions} />
        <Select label="Response" value={filters.response} onChange={set("response")} options={RESPONSE_OPTIONS} />
        <Select label="Stage" value={filters.stage} onChange={set("stage")} options={STAGE_OPTIONS} />
        <Select label="Accounts" value={filters.linked} onChange={set("linked")} options={LINK_OPTIONS} />
      </Tools>
      <LoadState state={list}>
        {(l) => (
          <>
            <Table
              columns={LIST_COLUMNS(openAccount)}
              rows={l.rows.map((r) => ({ key: r.id, ...r }))}
              emptyState="No dispute matches these filters."
            />
            <Pager page={page} limit={PAGE_SIZE} total={l.total} onPage={setPage} />
          </>
        )}
      </LoadState>
    </ReviewCard>
  );
}

// ── cells ───────────────────────────────────────────────────────────────────
const muted = { fontSize: 12, color: INK.muted };
const row = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };

function DaysLeft({ days }) {
  if (days === null || days === undefined) return null;
  const tone = days <= 3 ? "red" : days <= 7 ? "amber" : "slate";
  const label =
    days < 0 ? `${count(-days)} days overdue` : days === 0 ? "Due today" : days === 1 ? "1 day left" : `${count(days)} days left`;
  return <Pill tone={tone}>{label}</Pill>;
}

function AccountButton({ email, onOpen }) {
  return (
    <button
      type="button"
      style={{ ...linkButton, color: TONE.blue.text }}
      onClick={(e) => { e.stopPropagation(); onOpen(email); }}
    >
      {email}
    </button>
  );
}

/** The account the disputed deposit came from, then accounts with the same cardholder name. */
function DisputeAccounts({ r, onOpen }) {
  if (!r.account && !r.name_accounts?.length) return "—";
  return (
    <Wrap max={300}>
      {r.account && (
        <div style={row}>
          <AccountButton email={r.account} onOpen={onOpen} />
          <VerdictPill verdict={r.account_verdict} />
        </div>
      )}
      {r.name_accounts?.length > 0 && (
        <div style={muted}>
          Same name:{" "}
          {r.name_accounts.map((e, i) => (
            <React.Fragment key={e}>
              {i > 0 && ", "}
              <AccountButton email={e} onOpen={onOpen} />
            </React.Fragment>
          ))}
        </div>
      )}
    </Wrap>
  );
}

const StatePill = ({ state }) => <Pill tone={STATE_TONES[state] || "slate"}>{humanize(state)}</Pill>;

const respondBy = (r) => (
  <div style={{ ...row, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
    {utcDate(r.respond_by)}
    <DaysLeft days={r.days_left} />
  </div>
);

const caseCell = (r) => (
  <Wrap max={200}>
    <div>{r.network || "—"}</div>
    <div style={muted}>{r.case_id || "—"}</div>
  </Wrap>
);

const QUEUE_COLUMNS = (openAccount) => [
  { key: "respond_by", label: "Respond by", render: respondBy },
  { key: "amount", label: "Amount", numeric: true, render: (r) => moneyExact(r.amount) },
  { key: "buyer", label: "Cardholder" },
  {
    key: "reason",
    label: "Reason",
    render: (r) => (
      <Wrap max={180}>
        <div>{humanize(r.reason)}</div>
        <div style={muted}>{stageLabel(r.stage)}</div>
      </Wrap>
    ),
  },
  { key: "case", label: "Case", render: caseCell },
  { key: "occurred_at", label: "Deposit date", render: (r) => utcDate(r.occurred_at) },
  { key: "account", label: "Idenza account", render: (r) => <DisputeAccounts r={r} onOpen={openAccount} /> },
  { key: "action", label: "What Finix asks for", render: (r) => <Wrap max={380}>{r.action || r.message || "—"}</Wrap> },
];

const BREAKDOWN_COLUMNS = (key, title, label) => [
  { key, label: title, render: (r) => label(r[key]) },
  { key: "n", label: "Disputes", numeric: true, render: (r) => count(r.n) },
  { key: "usd", label: "Value", numeric: true, render: (r) => money(r.usd) },
  { key: "pending", label: "Pending", numeric: true, render: (r) => count(r.pending) },
  { key: "won", label: "Won", numeric: true, render: (r) => count(r.won) },
  { key: "lost", label: "Lost", numeric: true, render: (r) => count(r.lost) },
  { key: "win_rate", label: "Win rate", numeric: true, render: (r) => pct(r.win_rate) },
];

const MESSAGE_COLUMNS = [
  { key: "reason", label: "Reason", render: (r) => humanize(r.reason) },
  { key: "message", label: "Message", render: (r) => <Wrap max={640}>{r.message || "—"}</Wrap> },
  { key: "n", label: "Disputes", numeric: true, render: (r) => count(r.n) },
  { key: "usd", label: "Value", numeric: true, render: (r) => money(r.usd) },
];

const NAME_COLUMNS = (openAccount) => [
  {
    key: "name",
    label: "Cardholder name",
    render: (r) => (
      <div style={row}>
        {r.name}
        {r.returned && <Pill tone="red">Returned</Pill>}
      </div>
    ),
  },
  { key: "n", label: "Disputes", numeric: true, render: (r) => count(r.n) },
  { key: "usd", label: "Value", numeric: true, render: (r) => money(r.usd) },
  { key: "lost_usd", label: "Lost", numeric: true, render: (r) => money(r.lost_usd) },
  { key: "pending", label: "Pending", numeric: true, render: (r) => count(r.pending) },
  { key: "txns", label: "Deposits disputed", render: (r) => `${utcDate(r.first_txn, false)} to ${utcDate(r.last_txn)}` },
  {
    key: "accounts",
    label: "Idenza accounts",
    render: (r) =>
      r.accounts.length ? (
        <Wrap max={340}>
          {r.accounts.map((a) => (
            <div key={a.email} style={row}>
              <AccountButton email={a.email} onOpen={openAccount} />
              <VerdictPill verdict={a.verdict} />
              <span style={muted}>
                {a.how === "transfer" ? "by transfer" : "same name"}
                {a.returned ? `, used until ${utcDate(a.last_seen, false)}` : ""}
              </span>
            </div>
          ))}
        </Wrap>
      ) : (
        "—"
      ),
  },
];

const LIST_COLUMNS = (openAccount) => [
  { key: "created_at", label: "Filed", render: (r) => utcDate(r.created_at) },
  { key: "occurred_at", label: "Deposit date", render: (r) => utcDate(r.occurred_at) },
  { key: "amount", label: "Amount", numeric: true, render: (r) => moneyExact(r.amount) },
  { key: "buyer", label: "Cardholder" },
  { key: "reason", label: "Reason", render: (r) => humanize(r.reason) },
  { key: "stage", label: "Stage", render: (r) => stageLabel(r.stage) },
  { key: "state", label: "State", render: (r) => <StatePill state={r.state} /> },
  {
    key: "response_state",
    label: "Response",
    render: (r) =>
      r.response_state === "NEEDS_RESPONSE" ? <Pill tone="red">Needs response</Pill> : humanize(r.response_state),
  },
  { key: "respond_by", label: "Respond by", render: respondBy },
  { key: "case", label: "Case", render: caseCell },
  { key: "transfer", label: "Transfer" },
  { key: "account", label: "Idenza account", render: (r) => <DisputeAccounts r={r} onOpen={openAccount} /> },
];
